import * as React from "react";
import { Box, Stack, Typography, ButtonBase, CircularProgress } from "@mui/material";
import RocketLaunchRoundedIcon from "@mui/icons-material/RocketLaunchRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import { fetchGroupLaunchChoice, fetchMissionLaunchOptions, upsertGroupLaunchChoice } from "../../../api/queries";
import { subscribeToRows } from "../../../api/sheetSync";
import { sunSynchronousInclinationDeg } from "../../uplink/tle";
import { AuthorTip } from "../sheetAuthors";
import PhotoCredit from "../../uplink/PhotoCredit";
import type { EditedBy } from "../../../types/payload";
import type { GroupLaunchChoiceRow, LaunchOptionRow } from "../../../types/launch";

const MONO = "'Roboto Mono', 'Courier New', monospace";

const labelSx = {
  fontFamily: MONO,
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.12em",
  textTransform: "uppercase" as const,
  color: "rgba(196,181,253,0.6)",
};

// "525 km · 97.5° · sun-synchronous"
function describeOrbit(o: LaunchOptionRow): string {
  const sso = sunSynchronousInclinationDeg(o.orbit_altitude_km, o.orbit_eccentricity);
  const isSso = sso !== null && Math.abs(sso - o.orbit_inclination_deg) < 0.5;
  return `${Math.round(o.orbit_altitude_km)} km · ${o.orbit_inclination_deg}° inclination${isSso ? " · sun-synchronous" : ""}`;
}

function Fact(props: { label: string; value: string }) {
  return (
    <Stack spacing={0.25}>
      <Typography sx={labelSx}>{props.label}</Typography>
      <Typography sx={{ color: "rgba(226,232,240,0.9)", fontSize: 12.5, lineHeight: 1.45 }}>{props.value}</Typography>
    </Stack>
  );
}

// The launch section of the mission sheet: the mission's launch options as
// cards, and the group's pick among them, shared live with the other
// members like the rest of the sheet.
export default function LaunchChoicePanel(props: { groupId: string; missionId: string }) {
  const { groupId, missionId } = props;
  const [options, setOptions] = React.useState<LaunchOptionRow[]>([]);
  const [chosenId, setChosenId] = React.useState<string | null>(null);
  const [editedBy, setEditedBy] = React.useState<EditedBy>({});
  const [loading, setLoading] = React.useState(true);
  const [saveError, setSaveError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let mounted = true;
    fetchMissionLaunchOptions(missionId).then(({ data }) => {
      if (!mounted) return;
      setOptions(data ?? []);
      setLoading(false);
    });
    return () => {
      mounted = false;
    };
  }, [missionId]);

  React.useEffect(() => {
    let mounted = true;

    const apply = (row: GroupLaunchChoiceRow | null) => {
      setChosenId(row?.launch_option_id ?? null);
      setEditedBy(row?.edited_by ?? {});
    };

    const load = () =>
      fetchGroupLaunchChoice(groupId).then(({ data }) => {
        if (mounted) apply(data ?? null);
      });

    const unsubscribe = subscribeToRows<GroupLaunchChoiceRow>(
      "group_launch_choices",
      `group_id=eq.${groupId}`,
      (change) => {
        if (mounted && change.type !== "DELETE") apply(change.row);
      },
      load
    );
    load();

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, [groupId]);

  const choose = (optionId: string) => {
    if (optionId === chosenId) return;
    const previous = chosenId;
    setChosenId(optionId);
    setSaveError(null);
    upsertGroupLaunchChoice(groupId, optionId).then(({ error }) => {
      if (error) {
        setChosenId(previous);
        setSaveError("Your choice couldn't be saved. Please try again.");
      }
    });
  };

  return (
    <Box
      sx={{
        borderRadius: 3,
        p: { xs: 2.75, sm: 3.5 },
        border: "1px solid rgba(168,85,247,0.24)",
        background: "linear-gradient(160deg, rgba(255,255,255,0.055) 0%, rgba(255,255,255,0.015) 100%)",
        backdropFilter: "blur(14px)",
      }}
    >
      <Stack spacing={2.25}>
        <Stack spacing={0.75}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <RocketLaunchRoundedIcon sx={{ color: "#A78BFA", fontSize: 22 }} />
            <Typography sx={{ color: "#FFFFFF", fontWeight: 800, fontSize: { xs: 16, sm: 17.5 } }}>Launch</Typography>
          </Stack>
          <Typography sx={{ color: "rgba(226,232,240,0.7)", fontSize: 13.5, lineHeight: 1.6 }}>
            Your satellite needs a ride to space. Choose how it gets there: the rocket decides which orbit your satellite ends
            up in, and when.
          </Typography>
        </Stack>

        {saveError && <Typography sx={{ color: "#FCA5A5", fontSize: 13 }}>{saveError}</Typography>}

        {loading ? (
          <Stack alignItems="center" sx={{ py: 3 }}>
            <CircularProgress size={26} sx={{ color: "#C4B5FD" }} />
          </Stack>
        ) : options.length === 0 ? (
          <Typography sx={{ color: "rgba(226,232,240,0.6)", fontSize: 13.5 }}>
            The launch options for this mission are still being prepared.
          </Typography>
        ) : (
          <AuthorTip editedBy={editedBy} field="launch_option_id">
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: `repeat(${Math.min(options.length, 3)}, minmax(0, 1fr))` }, gap: 2 }}>
              {options.map((o) => {
                const chosen = o.id === chosenId;
                return (
                  <ButtonBase
                    key={o.id}
                    onClick={() => choose(o.id)}
                    aria-pressed={chosen}
                    sx={{
                      display: "block",
                      textAlign: "left",
                      borderRadius: 2.5,
                      overflow: "hidden",
                      border: chosen ? "2px solid #A78BFA" : "1px solid rgba(168,85,247,0.25)",
                      bgcolor: chosen ? "rgba(124,58,237,0.16)" : "rgba(255,255,255,0.03)",
                      transition: "border-color 0.2s, background-color 0.2s",
                      "&:hover": { bgcolor: chosen ? "rgba(124,58,237,0.2)" : "rgba(168,85,247,0.08)" },
                    }}
                  >
                    {o.image_url && (
                      <Box sx={{ position: "relative" }}>
                        <Box
                          component="img"
                          src={o.image_url}
                          alt={`${o.vehicle || o.name} launch`}
                          sx={{ width: "100%", height: 150, objectFit: "cover", display: "block" }}
                        />
                        <PhotoCredit credit={o.image_credit} />
                      </Box>
                    )}
                    <Stack spacing={1.5} sx={{ p: 2 }}>
                      <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                        <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                          <Typography sx={{ color: "#FFFFFF", fontWeight: 800, fontSize: 15.5 }}>{o.name}</Typography>
                          <Typography sx={{ color: "#C4B5FD", fontSize: 12.5, fontWeight: 700 }}>
                            {[o.vehicle, o.provider].filter(Boolean).join(" · ")}
                          </Typography>
                        </Stack>
                        {chosen ? (
                          <CheckCircleRoundedIcon sx={{ color: "#A78BFA", fontSize: 22, flexShrink: 0 }} />
                        ) : (
                          <RadioButtonUncheckedRoundedIcon sx={{ color: "rgba(226,232,240,0.35)", fontSize: 22, flexShrink: 0 }} />
                        )}
                      </Stack>
                      {o.summary && (
                        <Typography sx={{ color: "rgba(226,232,240,0.72)", fontSize: 12.5, lineHeight: 1.55 }}>{o.summary}</Typography>
                      )}
                      <Stack spacing={1}>
                        <Fact label="Delivers your satellite to" value={describeOrbit(o)} />
                        {o.launch_site && <Fact label="Launch site" value={o.launch_site} />}
                        {o.price_text && <Fact label="Price" value={o.price_text} />}
                      </Stack>
                    </Stack>
                  </ButtonBase>
                );
              })}
            </Box>
          </AuthorTip>
        )}
      </Stack>
    </Box>
  );
}
